// src/components/liability/LiabilitiesTable.tsx
import React from 'react';
import { Edit, Trash2, X } from 'lucide-react';
import { LiabilityData, ColumnKey } from '../../pages/Liability'; // Adjust path

interface LiabilitiesTableProps {
  paginatedData: LiabilityData[];
  visibleColumns: ColumnKey[];
  getColumnDisplayName: (col: ColumnKey) => string;
  getSortIndicator: (key: ColumnKey) => string;
  handleSort: (key: ColumnKey) => void;
  columnFilters: Partial<Record<ColumnKey, string>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<Partial<Record<ColumnKey, string>>>>;
  selectedRows: string[];
  handleSelectAll: () => void;
  handleRowSelect: (id: string) => void;
  handleEdit: (liability: LiabilityData) => void;
  handleDelete: (id: string) => void;
  formatCurrency: (value: string | number) => string;
  formatDate: (date: string) => string;
  getLabel: (group: string, value: string) => string;
  lookupGroupMap: Record<string, string>;
}

const LiabilitiesTable: React.FC<LiabilitiesTableProps> = (props) => {
  const {
    paginatedData, visibleColumns, getColumnDisplayName, getSortIndicator, handleSort,
    columnFilters, setColumnFilters, selectedRows, handleSelectAll, handleRowSelect,
    handleEdit, handleDelete, formatCurrency, formatDate, getLabel, lookupGroupMap
  } = props;

  return (
    <div className="overflow-x-auto">
      <table className="w-full divide-y divide-gray-200 min-w-max">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
              <input type="checkbox" checked={selectedRows.length === paginatedData.length && paginatedData.length > 0} onChange={handleSelectAll} className="rounded text-red-600 focus:ring-red-600" />
            </th>
            {visibleColumns.map(col => (
              <th key={col} className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100">
                <div className="flex items-center justify-between" onClick={() => handleSort(col)}>
                  <span className="mr-2">{getColumnDisplayName(col)}</span>
                  <span className="text-xs text-gray-400">{getSortIndicator(col)}</span>
                </div>
                <input
                  type="text"
                  placeholder={`Filter...`}
                  value={columnFilters[col] || ''}
                  onClick={e => e.stopPropagation()}
                  onChange={e => setColumnFilters(prev => ({ ...prev, [col]: e.target.value }))}
                  className="mt-1 w-full p-1 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-red-600"
                />
              </th>
            ))}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {paginatedData.map(liability => (
            <tr key={liability.id} className={`hover:bg-gray-50 ${selectedRows.includes(liability.id) ? 'bg-red-50' : ''}`}>
              <td className="px-6 py-4 whitespace-nowrap">
                <input type="checkbox" checked={selectedRows.includes(liability.id)} onChange={() => handleRowSelect(liability.id)} className="rounded text-red-600 focus:ring-red-600" />
              </td>
              {visibleColumns.map(col => (
                <td key={col} className="px-6 py-4 whitespace-nowrap text-sm">
                  { lookupGroupMap[col] 
                    ? getLabel(lookupGroupMap[col], liability[col as keyof LiabilityData] as string)
                    : ['liabilityTotalAmount', 'liabilityOutstandingAmount', 'liabilityInstalmentAmount'].includes(col)
                    ? <span className="text-red-600 font-medium">{formatCurrency(liability[col as keyof LiabilityData] as string)}</span>
                    : ['liabilityStartDate', 'liabilityEndDate'].includes(col)
                    ? formatDate(liability[col as keyof LiabilityData] as string)
                    : liability[col as keyof LiabilityData] || 'N/A'
                  }
                </td>
              ))}
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex items-center space-x-2">
                    <button onClick={() => handleEdit(liability)} className="text-red-600 hover:text-red-800 p-1" title="Edit"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(liability.id)} className="text-red-600 hover:text-red-800 p-1" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default LiabilitiesTable;